/**
 * The little pictures on teach cards and tips (#1838) — the approved mockup's
 * drawings, in the app's own tokens so they read in light and dark. The
 * pictures do the teaching; the line under each is a label.
 *
 * Each one is a tiny static sketch of the real screen: no data, no hooks.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { PicId } from "./features";

const Card = ({ children, className }: { children: ReactNode; className?: string }) => (
    <div className={cn("rounded-md border border-border bg-card p-2", className)}>{children}</div>
);
const Lbl = ({ children, className }: { children: ReactNode; className?: string }) => (
    <span className={cn("font-mono text-[10px] uppercase tracking-wide text-muted-foreground", className)}>
        {children}
    </span>
);
const Row = ({ children }: { children: ReactNode }) => (
    <div className="flex items-center justify-between gap-1.5 border-b border-border py-1 last:border-b-0">
        {children}
    </div>
);
type Tone = "plain" | "act" | "good" | "lock" | "gold";
const BIG: Record<Tone, string> = {
    plain: "border-border bg-card text-foreground",
    act: "border-transparent bg-orange-700 text-white",
    good: "border-transparent bg-success/10 text-success ring-1 ring-success",
    lock: "border-dashed border-border bg-muted text-muted-foreground",
    gold: "border-transparent bg-primary text-primary-foreground",
};
const Big = ({ children, tone = "plain" }: { children: ReactNode; tone?: Tone }) => (
    <span
        className={cn("mt-1 block w-full rounded-md border px-2 py-1.5 text-center text-[13px] font-bold", BIG[tone])}
    >
        {children}
    </span>
);
const PILL: Record<"act" | "good" | "gold" | "plain", string> = {
    act: "bg-orange-700/15 text-orange-800 dark:text-orange-300",
    good: "bg-success/15 text-success",
    gold: "bg-primary/15 text-primary",
    plain: "bg-muted text-foreground",
};
const Pill = ({ children, tone = "plain" }: { children: ReactNode; tone?: keyof typeof PILL }) => (
    <span className={cn("whitespace-nowrap rounded-full px-2 py-px text-[11px] font-bold", PILL[tone])}>
        {children}
    </span>
);
/** A mail-type tile: the colored square that says what an email is. */
const Tile = ({ c, children }: { c: string; children: ReactNode }) => (
    <span className={cn("grid h-4 w-4 flex-none place-items-center rounded text-[9px] font-bold text-white", c)}>
        {children}
    </span>
);
const MAIL_ROWS: Array<[string, string, string]> = [
    ["bg-sky-600", "WO", "Cintas · new work order"],
    ["bg-emerald-700", "$", "FWISD · payment"],
    ["bg-violet-700", "?", "Kroger · question"],
];
/** The top bar's Inbox chip: gray at 0, orange with a number. */
const InboxChip = ({ n }: { n: number }) => (
    <span
        className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold",
            n > 0 ? "bg-orange-700 text-white" : "bg-white/15 text-stone-200",
        )}
    >
        ✉ Inbox {n > 0 ? n : "✓"}
    </span>
);
/** A person's initials in a circle (whose email it is). */
const Face = ({ children }: { children: ReactNode }) => (
    <span className="grid h-5 w-5 flex-none place-items-center rounded-full bg-primary/20 text-[9px] font-bold text-primary">
        {children}
    </span>
);
/** The dark app bar / glance band, the same in both themes. */
const Band = ({ children, className }: { children: ReactNode; className?: string }) => (
    <div
        className={cn(
            "flex flex-wrap items-center gap-1.5 rounded-md bg-stone-800 px-2 py-1.5 text-stone-100",
            className,
        )}
    >
        {children}
    </div>
);
const BandPill = ({ children, hot }: { children: ReactNode; hot?: boolean }) => (
    <span
        className={cn(
            "whitespace-nowrap rounded-full px-2 py-px text-[11px] font-bold",
            hot ? "bg-orange-700 text-white" : "bg-white/15 text-stone-100",
        )}
    >
        {children}
    </span>
);
const Kbd = ({ children }: { children: ReactNode }) => (
    <span className="rounded border border-b-2 border-border bg-card px-1 font-mono text-[11px]">{children}</span>
);
const Col = ({ children, focus }: { children: ReactNode; focus?: boolean }) => (
    <div className={cn("min-h-[64px] rounded border border-border bg-card p-1", focus && "ring-2 ring-primary")}>
        {children}
    </div>
);
const Tok = ({ children, good }: { children: ReactNode; good?: boolean }) => (
    <div
        className={cn(
            "mt-1 rounded px-1 text-[11px] font-semibold",
            good ? "bg-success/15 text-success" : "bg-primary/15 text-primary",
        )}
    >
        {children}
    </div>
);
/** A left color bar (a span, so it keeps the card's rounded corner). */
const BAR: Record<"todo" | "there" | "done" | "needs", string> = {
    todo: "bg-stone-400",
    there: "bg-sky-600",
    done: "bg-success",
    needs: "bg-orange-700",
};
const Bar = ({ tone }: { tone: keyof typeof BAR }) => (
    <span aria-hidden className={cn("absolute inset-y-0 left-0 w-1 rounded-l", BAR[tone])} />
);
const GridCard = ({ tone, children }: { tone: keyof typeof BAR; children: ReactNode }) => (
    <div className="relative mt-1 rounded bg-card py-0.5 pl-2.5 pr-1.5 text-[11px] font-semibold">
        <Bar tone={tone} />
        {children}
    </div>
);

export const PICTURES: Record<PicId, () => ReactNode> = {
    // No Job Left Open — the iPad
    njlo1: () => (
        <Card>
            <span className="font-bold text-orange-800 dark:text-orange-300">⚠ You left a job open</span>
            <br />
            <b>Chuy&apos;s Arlington</b>
            <br />
            <Lbl>left 2:14 PM · 1 of 3 tested</Lbl>
        </Card>
    ),
    njlo2: () => (
        <div>
            <Big>↻ Coming back</Big>
            <Big>✕ Can&apos;t finish</Big>
            <Big tone="good">✓ Done</Big>
        </div>
    ),
    njlo3: () => (
        <div>
            <Lbl>Next: Taco Bell #4471</Lbl>
            <Big tone="lock">🔒 On my way</Big>
            <Lbl className="mt-1.5 block text-orange-800 dark:text-orange-300">Answer Chuy&apos;s first</Lbl>
        </div>
    ),
    // No Job Left Open — the office list
    lo1: () => (
        <Band>
            <BandPill>Today 14</BandPill>
            <BandPill>Missed 2</BandPill>
            <BandPill hot>Left open 3</BandPill>
        </Band>
    ),
    lo2: () => (
        <Card>
            <Row>
                <span>Chuy&apos;s Arlington</span>
                <Pill tone="gold">↻ Coming back</Pill>
            </Row>
            <Row>
                <span>Kroger #512</span>
                <Pill tone="good">✓ Done</Pill>
            </Row>
        </Card>
    ),
    lo3: () => (
        <Card>
            <Row>
                <span>AutoZone #6269</span>
                <Pill tone="act">No answer</Pill>
            </Row>
            <Big tone="act">Call the tech</Big>
        </Card>
    ),
    // The new job page
    jpGlance: () => (
        <div className="rounded-md bg-stone-800 p-2 text-stone-100">
            <div className="text-[10px] uppercase tracking-wide text-stone-400">Test · #092678361</div>
            <div className="text-[13px] font-bold">Chuy&apos;s Arlington</div>
            <div className="mt-1 flex gap-1">
                <BandPill>3 devices</BandPill>
                <BandPill hot>1 not tested</BandPill>
            </div>
            <div className="mt-1.5 flex gap-1">
                <BandPill>⇄ Move to ▾</BandPill>
                <BandPill>📌 City filing</BandPill>
            </div>
        </div>
    ),
    jpMove: () => (
        <div>
            <Big>⇄ Move to ▾</Big>
            <Card className="mt-1">
                <Row>
                    <span>Awaiting scheduling</span>
                </Row>
                <Row>
                    <b>Office review</b>
                </Row>
                <Row>
                    <span>Closed</span>
                </Row>
            </Card>
        </div>
    ),
    jpCmdk: () => (
        <Card>
            <Kbd>⌘</Kbd> <Kbd>K</Kbd> <span className="text-muted-foreground">chuy…</span>
            <Row>
                <b>Chuy&apos;s Arlington #361</b>
            </Row>
            <Row>
                <span>Send task about this job</span>
                <span>📌</span>
            </Row>
        </Card>
    ),
    jpPins: () => (
        <Band>
            <span className="text-[11px] text-stone-400">Your pins</span>
            <BandPill>📌 City filing</BandPill>
            <BandPill>📌 Tech brief</BandPill>
        </Band>
    ),
    // Points — the tech's
    pb1: () => (
        <div>
            <div className="grid grid-cols-3 gap-1">
                <Col>
                    <Lbl>Earned</Lbl>
                    <Tok>+4</Tok>
                    <Tok>+2</Tok>
                </Col>
                <Col>
                    <Lbl>Waiting</Lbl>
                    <Tok>+3</Tok>
                </Col>
                <Col>
                    <Lbl>Approved</Lbl>
                    <Tok good>+6</Tok>
                </Col>
            </div>
            <Lbl className="mt-1 block text-right">your pay period</Lbl>
        </div>
    ),
    pb2: () => (
        <div className="grid grid-cols-3 gap-1">
            <Col>{null}</Col>
            <Col focus>
                <Lbl className="text-primary">Waiting on report</Lbl>
                <Tok>#361 +3</Tok>
                <Tok>#498 +2</Tok>
            </Col>
            <Col>{null}</Col>
        </div>
    ),
    pb3: () => (
        <Card>
            <Row>
                <span>📄 City report uploaded</span>
            </Row>
            <Row>
                <span>#361 +3</span>
                <Pill tone="good">→ Approved</Pill>
            </Row>
        </Card>
    ),
    // Points — the office Point Board
    pbo1: () => (
        <Card>
            {[
                ["Chuy", 70, 20],
                ["Tristian", 50, 35],
                ["Mike", 85, 5],
            ].map(([n, a, w]) => (
                <div key={n as string} className="flex items-center gap-1.5 py-0.5">
                    <span className="w-12 truncate text-[11px]">{n}</span>
                    <div className="flex h-2.5 flex-1 overflow-hidden rounded bg-muted">
                        <div className="bg-sky-600" style={{ width: `${a}%` }} />
                        <div className="bg-orange-500" style={{ width: `${w}%` }} />
                    </div>
                </div>
            ))}
            <Lbl className="block text-right">goal 200</Lbl>
        </Card>
    ),
    pbo2: () => (
        <Card>
            <Lbl className="text-primary">Waiting on report</Lbl>
            <Row>
                <span>#361 Arlington</span>
                <Pill tone="act">city paper</Pill>
            </Row>
            <Row>
                <span>#498 Keller</span>
                <Pill tone="act">city paper</Pill>
            </Row>
        </Card>
    ),
    pbo3: () => (
        <Card>
            <Row>
                <span>📄 City report on job</span>
            </Row>
            <Row>
                <span>#361 +3</span>
                <Pill tone="good">→ Approved</Pill>
            </Row>
        </Card>
    ),
    // The new Grid
    grid1: () => (
        <Card>
            <GridCard tone="todo">Taco Bell · to do</GridCard>
            <GridCard tone="there">Kroger · tech there</GridCard>
            <GridCard tone="done">AutoZone · finished</GridCard>
            <GridCard tone="needs">Chuy&apos;s · needs a person</GridCard>
        </Card>
    ),
    grid2: () => (
        <Card>
            <Lbl>Tray</Lbl>
            <Row>
                <span className="text-[11px]">Sonic #88</span>
                <Pill tone="good">✓ Ready</Pill>
            </Row>
            <Row>
                <span className="text-[11px]">Kroger #512</span>
                <Pill tone="act">Parts?</Pill>
            </Row>
        </Card>
    ),
    grid3: () => (
        <div className="relative">
            <GridCard tone="there">Kroger · tech there</GridCard>
            <Card className="ml-6 mt-1 shadow-md">
                <Row>
                    <span className="text-[11px]">Open job</span>
                </Row>
                <Row>
                    <span className="text-[11px]">Move to tomorrow</span>
                </Row>
            </Card>
        </div>
    ),
    gridFit: () => (
        <Card>
            <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>7a</span>
                <span>→</span>
                <span>4p</span>
            </div>
            <GridCard tone="todo">8:00 Sonic</GridCard>
            <GridCard tone="todo">1:30 Kroger</GridCard>
        </Card>
    ),
    gridViews: () => (
        <Card>
            <Row>
                <span>🗺 Map</span>
            </Row>
            <Row>
                <span>🧭 Route planner</span>
            </Row>
        </Card>
    ),
    gridWeek: () => (
        <div className="grid grid-cols-5 gap-0.5">
            {[5, 7, 2, 6, 4].map((n, i) => (
                <div key={i} className="rounded border border-border bg-card p-0.5 text-center">
                    <div className="text-[9px] text-muted-foreground">{"MTWTF"[i]}</div>
                    <div className={cn("text-[12px] font-bold", n < 3 && "text-orange-800 dark:text-orange-300")}>
                        {n}
                    </div>
                </div>
            ))}
        </div>
    ),
    // The friendly Mailroom
    mail3: () => (
        <Card>
            <div className="flex items-center gap-1.5">
                <Tile c="bg-violet-700">?</Tile>
                <span className="truncate text-[11px] font-semibold">Kroger · question</span>
            </div>
            <Big tone="act">Reply</Big>
        </Card>
    ),
    chip1: () => (
        <Band className="justify-end">
            <InboxChip n={0} />
        </Band>
    ),
    chip2: () => (
        <Band className="justify-end">
            <InboxChip n={5} />
        </Band>
    ),
    chip3: () => (
        <Card>
            {MAIL_ROWS.slice(0, 2).map(([c, i, t], k) => (
                <Row key={t}>
                    <span className="flex min-w-0 items-center gap-1.5">
                        <Tile c={c}>{i}</Tile>
                        <span className="truncate text-[11px]">{t}</span>
                    </span>
                    <span className="rounded bg-orange-700 px-1.5 text-[11px] font-bold text-white">
                        {k === 0 ? "Make job" : "Open"}
                    </span>
                </Row>
            ))}
        </Card>
    ),
    list1: () => (
        <div className="flex flex-wrap gap-1">
            <span className="rounded-full bg-orange-700 px-2 py-0.5 text-[11px] font-bold text-white">Needs me 3</span>
            <Pill>Team 8</Pill>
            <Pill>Waiting 4</Pill>
            <Pill>Done</Pill>
        </div>
    ),
    list2: () => (
        <Card>
            <div className="flex items-center gap-1.5">
                <Tile c="bg-sky-600">WO</Tile>
                <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">Cintas · new WO</span>
                <Face>DS</Face>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full w-2/3 rounded-full bg-orange-700" />
            </div>
            <div className="mt-0.5 text-right text-[10px] text-muted-foreground">2 days</div>
        </Card>
    ),
    wit1: () => (
        <Card>
            <div className="flex items-center gap-1.5">
                <Tile c="bg-sky-600">WO</Tile>
                <span className="text-[11px] font-bold">Work order</span>
            </div>
            <div className="mt-1 text-[11px]">Cintas wants a test at store #6269.</div>
        </Card>
    ),
    wit2: () => (
        <Card>
            <Row>
                <span className="text-[11px]">Customer: Cintas</span>
                <Pill tone="good">sure</Pill>
            </Row>
            <Row>
                <span className="text-[11px]">Site: store #6269</span>
                <Pill tone="act">check</Pill>
            </Row>
        </Card>
    ),
    wit3: () => (
        <Card>
            <div className="text-[11px] font-semibold">New work order. Make the job.</div>
            <Big tone="act">Send to Intake Desk</Big>
        </Card>
    ),
    // The Quiet Toolbar (10/10): marks on the thing, three honest chips, history.
    quiet1: () => (
        <Card>
            <Row>
                <span className="text-[11px] font-semibold">Chain Store #6269</span>
                <span className="rounded-full bg-red-100 px-1.5 text-[10px] font-bold text-red-800 dark:bg-red-950 dark:text-red-200">
                    ⚑ Blocked
                </span>
            </Row>
            <Row>
                <span className="text-[11px] font-semibold">Call Cintas</span>
                <span className="rounded-full bg-emerald-100 px-1.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                    ↩ Came back
                </span>
            </Row>
        </Card>
    ),
    quiet2: () => (
        <Band className="flex flex-wrap items-center gap-1">
            <InboxChip n={4} />
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-600 px-2 py-0.5 text-[11px] font-bold text-white">
                Tasks 4
            </span>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-900">
                ⚠ Stuck 1
            </span>
        </Band>
    ),
    quiet3: () => (
        <Card>
            <Lbl>Your menu</Lbl>
            <div className="text-[12px]">Profile</div>
            <div className="text-[12px] font-bold">Recent activity</div>
            <div className="text-[12px]">Settings</div>
        </Card>
    ),
    // Help Desk + Tasks
    helpdesk: () => (
        <Card>
            <Lbl>You</Lbl>
            <div className="text-[12px]">Job won&apos;t send to the city</div>
            <Lbl className="mt-1 block">Help Desk</Lbl>
            <div className="text-[12px]">Report was filed as &ldquo;Document&rdquo;.</div>
            <Big tone="good">Fix it: file as official ✓</Big>
        </Card>
    ),
    helpdeskTech: () => (
        <div className="grid grid-cols-2 gap-1">
            {["Something's broken", "Can't find it", "App won't let me", "Other"].map((l, i) => (
                <span
                    key={l}
                    className={cn(
                        "rounded-md border px-1 py-1.5 text-center text-[11px] font-semibold",
                        i === 2
                            ? "border-transparent bg-primary/15 text-primary ring-2 ring-primary"
                            : "border-border bg-card",
                    )}
                >
                    {l}
                </span>
            ))}
        </div>
    ),
    // Shared job notes (Closeout, 10/16)
    notes1: () => (
        <Card>
            <div className="flex items-center gap-1.5 font-bold">
                <span className="text-[15px] text-amber-500">★</span> Americold Railhead
            </div>
            <div className="mt-1 truncate rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900 dark:text-amber-200">
                Darcy · Oct 14: waiting on PO from Cintas
            </div>
        </Card>
    ),
    notes2: () => (
        <Card>
            <div className="mb-1 flex gap-1">
                <Face>D</Face>
                <Face>R</Face>
                <Face>C</Face>
            </div>
            <div className="truncate text-[11px] font-semibold">★ Darcy · Oct 14: waiting on PO</div>
            <Lbl>The office sees it</Lbl>
        </Card>
    ),
    notes3: () => (
        <Card>
            <div className="truncate text-[11px] text-muted-foreground">★ a note stays a note</div>
            <span className="mt-1 block w-full rounded-md bg-pink-700 px-2 py-1.5 text-center text-[13px] font-bold text-white">
                Hand to… Corbin
            </span>
        </Card>
    ),
    // Add from the field — the tech's green +
    fa1: () => (
        <div className="flex items-center justify-end gap-2">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-green-700 text-xl font-bold text-white">
                +
            </span>
            <span className="grid h-10 w-10 place-items-center rounded-full bg-primary text-[15px] text-primary-foreground">
                ✉
            </span>
        </div>
    ),
    fa2: () => (
        <div className="grid grid-cols-2 gap-1 text-[11px] font-bold">
            <span className="rounded-md border-2 border-blue-700 bg-blue-50 px-1 py-1 text-center text-blue-900 dark:bg-blue-500/15 dark:text-blue-100">
                This job
            </span>
            <span className="rounded-md border border-border px-1 py-1 text-center">Device</span>
            <span className="rounded-md border border-border px-1 py-1 text-center">This location</span>
            <span className="rounded-md border border-border px-1 py-1 text-center">This customer</span>
        </div>
    ),
    fa3: () => (
        <div>
            <Big tone="good">✓ Photo saved</Big>
            <span className="mt-1 block rounded-md bg-amber-100 px-2 py-1 text-center text-[11px] font-bold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
                🔒 The customer never sees it
            </span>
        </div>
    ),
    fao1: () => (
        <Card>
            <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-bold text-green-800 dark:bg-green-500/15 dark:text-green-200">
                From the field · 1
            </span>
            <div className="mt-1 text-[11px]">“Gate code changed to 4471”</div>
            <Lbl>Luis · 10/26 · Job #102678702</Lbl>
        </Card>
    ),
    fao2: () => (
        <div className="flex justify-center">
            <span className="rounded-lg border border-red-700 bg-red-50 px-2.5 py-1 text-[12px] font-bold text-red-800 dark:bg-red-500/15 dark:text-red-200">
                ⚑ From the field · 2
            </span>
        </div>
    ),
    fao3: () => (
        <div>
            <Big>Move</Big>
            <span className="mt-1 block rounded-md bg-amber-100 px-2 py-1 text-center text-[11px] font-bold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
                🔒 Never goes to the customer
            </span>
        </div>
    ),
    tasks: () => (
        <Card>
            <Row>
                <span className="text-[11px]">Call Arlington about #361</span>
                <Pill tone="gold">Sent → Darcy</Pill>
            </Row>
            <Row>
                <span className="text-[11px]">Order RK957-RV</span>
                <Pill tone="good">Done · confirm?</Pill>
            </Row>
        </Card>
    ),
};

/** The picture frame every card and tip uses. */
export function TeachPicture({ pic, className }: { pic: PicId; className?: string }) {
    const Pic = PICTURES[pic];
    return (
        <div
            className={cn(
                "relative grid place-items-center overflow-hidden rounded-lg border border-border bg-background p-2.5 text-[12px] leading-tight text-foreground",
                className,
            )}
            aria-hidden
        >
            <div className="w-full">{Pic ? Pic() : null}</div>
        </div>
    );
}
